<template>
  <div class="avatarContentEditionPanel">
    <div class="avatarContentEditionContainer">
      <div class="avatarContentEditorTitle">
        {{ $t('aiAvatar.script') }}
        <b-dropdown aria-role="list" id="buttonPauseDropDown" class="pauseButtonContent">
          <template #trigger="{ active }">
            <w-button icon="timer-plus-outline"
                      size="small"
                      color="outlined"
                      :tooltip="$t('aiAvatar.tooltip.addPause')">
              {{ $t('aiAvatar.addPause') }}
            </w-button>
          </template>
          <b-dropdown-item aria-role="listitem"  @click="insertPause(pauseDurations.EXTRA_WEAK)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.extraWeak') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPause(pauseDurations.WEAK)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.weak') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPause(pauseDurations.MEDIUM)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.medium') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPause(pauseDurations.STRONG)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.strong') }}</span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem"  @click="insertPause(pauseDurations.EXTRA_STRONG)" class="lineButtonGenerate"><span class="labelButtonGenerate">{{ $t('aiAvatar.pause.extraStrong') }}</span></b-dropdown-item>
          <w-divider/>
          <b-dropdown-item aria-role="listitem" class="lineButtonGenerate" >
            <div class="pauseCustomTitle">{{ $t('aiAvatar.pause.custom') }}</div>
            <div class="pauseCustomLine">
              <div @click.stop><w-input v-model.number="customPause" placeholder="3.5" size="small"/></div>
              <w-button @click="insertPause(customPause * 1000)" size="small" color="outlined" :enabled="isCustomPauseValid">{{ $t('aiAvatar.pause.customAdd') }}</w-button>
            </div>
            <div v-show="!isCustomPauseValid">{{ $t('aiAvatar.pause.customError') }}</div>
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <!-- INPUT TEXT -->
      <w-input type="textarea" v-model="textToGenerate" @input="onTextInput" class="contentTextAreaAI"
               :class="{overtext:textToGenerate.length>maxlengthOfCharacter}"
               :placeholder="$t('aiAvatar.content.placeholder')"
               :maxlength="maxlengthOfCharacter"></w-input>

      <div v-if="ssmlErrors !== ''" class="errorSSML">
        <w-error :message="ssmlErrors"/>
      </div>

      <!-- SELECT LANGUAGE -->
      <div class="lineSelect">
        <div class="labelSelect">{{ $t('aiAvatar.langsSelector') }}</div>
        <w-select class="langSelector" size="small" @input="onAvatarLangChange" :value="langSelected">
          <option :value="listOfVoicesLangs[0].value" :key="'voice_lang_'+listOfVoicesLangs[0].value">
            {{ listOfVoicesLangs[0].label }}
          </option>
          <hr>
          <option v-for="lang in listOfVoicesLangs.slice(1)" :value="lang.value" :key="'voice_lang_'+lang.value">
            {{ lang.label }}
          </option>
        </w-select>
      </div>

      <!-- SELECT ACCENT (regional variant of the language, supported by the picked voice's provider) -->
      <div class="lineSelect" v-if="accentOptions.length">
        <div class="labelSelect">{{ $t('voiceSelector.accent') }}</div>
        <w-select class="langSelector" size="small" :value="accentSelected || ''" @input="onAccentInput">
          <option :value="''">{{ $t('voiceSelector.defaultAccent') }}</option>
          <option v-for="accent in accentOptions" :value="accent.locale" :key="'accent_'+accent.locale">
            {{ accent.label }}
          </option>
        </w-select>
      </div>

      <!-- SELECT VOICES -->
      <div class="lineSelect last">
        <div class="labelSelect labelVoiceSelector">{{ $t('aiAvatar.voicesSelector') }}</div>
        <w-button class="voiceSelector" size="small" color="outlined" :expanded="true"
                  icon-right="chevron-right" @click="voiceModalActive = true">
          {{ voiceSelectedLabel }}
        </w-button>

        <w-button :icon="playingVoice?'stop':'volume-high'" :enabled="textToGenerate && !ssmlErrors"
                  @click="previewVoice" :tooltip="$t('aiAvatar.tooltip.previewVoice')"
                  class="buttonPlayPreview" size="small" color="outlined"
                  :loading="loadingVoice"></w-button>
      </div>
      <voice-selector-modal :active.sync="voiceModalActive" :voice="voiceSelected" :gender="gender"
                            :language="effectiveLang"
                            @validate="onVoiceValidated"/>


      <div class="lineButton" v-if="slides.length > 0">
        <b-dropdown aria-role="list"  id="buttonDropDown" position="is-top-right">
          <template #trigger="{ active }">
            <w-button class="buttonOneSlide" :expanded="true" :icon-right="active ? 'menu-up' : 'menu-down'"  :enabled="!!textToGenerate && !ssmlErrors"
                      :loading="isAvatarGenerating || loadingVoice"
                      color="primary" size="small">
              {{$t("aiAvatar.generate")}}
            </w-button>
          </template>
          <b-dropdown-item aria-role="listitem"  @click="generateSlide" class="lineButtonGenerate"><w-icon class="iconButtonsGenerate" :icon="selectedSegment?'file-replace-outline':'play-box-outline'"/>
            <span class="labelButtonGenerate" v-if="!selectedSegment">
            {{$t('aiAvatar.generateThisSlide')}}
            </span>
          <span v-else>
             {{ $t("aiAvatar.replaceThisSlide")}}
          </span></b-dropdown-item>
          <b-dropdown-item aria-role="listitem" v-if="slides.length > 1"  @click="generateAllSlide()" class="lineButtonGenerate" >
            <w-icon class="iconButtonsGenerate" icon="animation-play-outline"/>
            <span class="labelButtonGenerate">{{$t('aiAvatar.generateAllSlide')}}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="lineButton" v-else>
        <w-button class="buttonOneSlide" :expanded="true" @click="generateSlide" icon="play-box-outline" :enabled="!!textToGenerate && !ssmlErrors"
                  :loading="isAvatarGenerating || loadingVoice"
                  color="primary" size="small">
          {{$t("aiAvatar.generate")}}
        </w-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Component, Emit, Watch} from 'vue-property-decorator';
import store from '@/store';
import {Howl} from 'howler';
import VideoSource from '@/components/recorder/videoLayout/source/VideoSource.vue';
import WButton from '@/components/wrapper/w-button.vue';
import WIcon from '@/components/wrapper/w-icon.vue';
import WToolTip from '@/components/wrapper/w-toolTip.vue';
import WModal from '@/components/wrapper/w-modal.vue';
import CenterCenterPanel from '@/components/layout/CenterCenter.vue';
import WSwitch from '@/components/wrapper/w-switch.vue';
import WHelp from '@/components/wrapper/w-help.vue';
import CustomVBUploader from '@/components/recorder/sticker/buttons/CustomVBUploader.vue';
import {
  AvatarLanguage,
  LIST_OF_AVATAR,
  Text2SpeachParams
} from "@/store/AIAvatar/aiavatarGenerationModel";
import {
  AVATAR_EDITING_TEXT,
  SELECT_AI_ACCENT,
  SELECT_AI_AVATAR,
  SELECT_AI_BACKGROUND,
  SELECT_AI_LANG, SELECT_AI_VOICE
} from "@/store/AIAvatar/AIAVatarAction";
import WSelect from "@/components/wrapper/w-select.vue";
import UpgradePlanButton from "@/components/upgradePlan/UpgradePlanButton.vue";
import WInput from "@/components/wrapper/w-input.vue";
import { escapeForSSML, listOfBaseLanguages } from "@/utils/voiceUtil";
import {  MAX_LENGTH_AIAVATAR_PREMIUM } from "@/utils/workspaceUtil";
import { PREVIEW_TEXT_2_SPEECH_GENERATE, TEXT_2_SPEECH_GENERATE } from "@/store/media/mediaAction";
import {base64ToBlob} from "@/utils/audioUtil";
import {Slide, ScriptSlideLayout, SlideTemplate} from "@/store/slides2Video/slides2VideoModels";
import {clone} from "@/utils/cloneUtil";
import {updateCurrentSlide} from "@/utils/slides/slideUtil";
import WDivider from '@/components/wrapper/w-divider.vue';
import WError from '@/components/wrapper/w-error.vue';
import {checkValidSSML, generateErrorMessages} from '@/utils/ssmlUtil';
import {debounce} from 'lodash';
import { Segment } from "@/store/timeLine/timeEvent";
import VoiceSelectorModal from '@/components/recorder/voice/VoiceSelectorModal.vue';
import { getVoiceCatalog, TTSCatalog } from '@/store/media/ttsCatalogModel';
import { formatPauseTag, PauseDuration } from '@/utils/pauseTag';
import { ttsCreditsForVoice } from '@/utils/ttsCredits';
import { SpeachEvent, SpeachEventName, SpeachEventTtsUsage } from '@/speach/speachEvent';


@Component({
  components: {
    WDivider,
    WError,
    WInput,
    UpgradePlanButton,
    WSelect,
    VoiceSelectorModal,
    CustomVBUploader, WHelp, WSwitch, CenterCenterPanel, WModal, WToolTip, WIcon, WButton, VideoSource
  },
  computed: {},
})
export default class AIAvatarContentEditionPanel extends Vue {

  private playingVoice = false;
  private audioPlayer: Howl | null = null;
  private loadingVoice = false;
  private textToGenerate: string = "";
  private customPause: number = 2.5;
  private ssmlErrors: string = "";
  private voiceModalActive = false;
  // Expose the preset enum to the template so menu items reference named values, not magic numbers.
  private pauseDurations = PauseDuration;


  public mounted(){
    this.onCurrentSlideChange();
    this.checkVoiceGender();
  }

  public beforeDestroy() {
    // Cancel any pending debounced calls
    this.debouncedSSMLCheck.cancel();
    // Clean up audio player if needed
    this.stopPreviewVoice();
  }

  get isAvatarGenerating() {
    return store.getters.isAvatarGenerating
  }

  get isImageGenerationInProgress() {
    return store.getters.isImageGenerationInProgress
  }

  get selectedAvatar() {
    return store.getters.getAvatarSelected;
  }

  get slides(): Slide[] {
    return store.getters.slides;
  }

  get currentSlide(): Slide | null {
    if (store.getters.getCurrentSlideIndex > -1) {
      return store.getters.slides[store.getters.getCurrentSlideIndex];
    }
    return null;
  }
  get selectedSegment():Segment|null{
    return store.getters.getSelectedSegment
  }

  get langSelected() {
    return store.getters.getAvatarLangSelected || this.listOfVoicesLangs[0].value
  }

  get accentSelected(): string | null {
    return store.getters.getAvatarAccentSelected;
  }

  // Locale actually sent to TTS: the chosen accent (regional variant) over the base language.
  get effectiveLang(): string | null {
    return this.accentSelected || this.langSelected;
  }

  // True only for the layouts that actually show an avatar. In voice-only / slide layouts there is no
  // avatar on screen, so the (stale) last-selected avatar must not constrain the voices.
  get hasAvatar(): boolean {
    const layout = store.getters.getAvatarScriptSlideLayout;
    if (layout === ScriptSlideLayout.AVATAR_ONLY || layout === ScriptSlideLayout.AVATAR_BOTTOM_LEFT) {
      return true;
    }
    // A slide shows the avatar too, but only with the templates that reserve a
    // spot for it: the voice must then match that avatar like anywhere else.
    if (layout === ScriptSlideLayout.SLIDE) {
      const template = this.currentSlide?.slideTemplate;
      return template === SlideTemplate.AVATAR_LEFT || template === SlideTemplate.AVATAR_RIGHT;
    }
    return false;
  }

  get gender() {
    // No avatar in the current layout → no gender constraint: offer every voice (both genders).
    if (!this.hasAvatar) {
      return '';
    }
    return LIST_OF_AVATAR.find((av) => {
      return av.avatar === this.selectedAvatar
    })?.gender
  }

  get listOfVoicesLangs() {
    const autoDetectOption = {label: this.$t('generic.autodetect'), value: null};
    // One entry per base language. Regional variants are handled by the accent dropdown.
    return [autoDetectOption, ...listOfBaseLanguages];
  }

  // Provider of the currently selected voice (drives which accents apply).
  get selectedVoiceProvider(): string {
    return this.voicesCatalog.voices.find((v) => v.id === this.voiceSelected)?.provider || '';
  }

  // Accent options for the selected base language, limited to those the selected voice's provider
  // supports (Gemini also offers private-use accents, Azure only real locales). Empty on auto-detect.
  get accentOptions(): { locale: string, label: string }[] {
    if (!this.langSelected) {
      return [];
    }
    const base = String(this.langSelected).split('-')[0];
    const provider = this.selectedVoiceProvider;
    return this.voicesCatalog.accents.filter((a) =>
      a.base === base && (!provider || a.providers.indexOf(provider as any) !== -1));
  }

  get voicesCatalog(): TTSCatalog {
    return getVoiceCatalog(store.getters.isAdvancedTtsEnabled);
  }

  // Voices matching the current avatar's gender, driven by the static catalog. With no avatar
  // (voice-only / slide layout) gender is '' → every voice is offered, both genders.
  get listOfVoice() {
    return this.voicesCatalog.voices
      .filter((voice) => this.gender === '' || voice.gender === this.gender)
      .map((voice) => ({ label: voice.label, value: voice.id, gender: voice.gender, advanced: voice.advanced }));
  }

  get voiceSelected() {
    return store.getters.getAvatarVoiceSelected
  }

  // Label shown on the voice button - catalog-driven only. If the selected voice isn't in the catalog
  // (e.g. an advanced voice persisted while advanced TTS is now off), show the default it will be reset to,
  // never a stale name.
  get voiceSelectedLabel(): string {
    const found = this.voicesCatalog.voices.find((v) => v.id === this.voiceSelected);
    if (found) {
      return found.label;
    }
    return this.listOfVoice.length ? this.listOfVoice[0].label : "";
  }


  get maxlengthOfCharacter() {
    return MAX_LENGTH_AIAVATAR_PREMIUM;
    // if (this.isFreemium) {
    //   return MAX_LENGTH_AIAVATAR_FREEMIUM;
    // } else {
    //   return MAX_LENGTH_AIAVATAR_PREMIUM;
    // }
  }

  get isCustomPauseValid(): boolean {
    return this.customPause !== undefined && this.customPause > 0 && this.customPause <= 10;
  }

  /**
   * Validates SSML markup in the text-to-speech parameters
   *
   * @returns Promise resolving to a boolean indicating whether the SSML is valid
   */
  private async checkSSMLValidity(): Promise<boolean> {
    // Only proceed if there's a current slide and text has changed
    if (!this.currentSlide) return false;

    // Get validation errors from SSML checker
    const ssmlErrors = await checkValidSSML(escapeForSSML(this.textToGenerate));

    if (ssmlErrors.length === 0) {
      // No errors, so clear the error state
      this.ssmlErrors = "";
      return true;
    }

    // Only update if the error state actually changed
    const errorFormatted = generateErrorMessages(ssmlErrors);
    if (this.ssmlErrors !== errorFormatted) {
      this.ssmlErrors = errorFormatted;
    }

    return ssmlErrors.length === 0;
  }

  private debouncedSSMLCheck = debounce(() => {
    this.checkSSMLValidity();
  }, 1000); // debounce time in milliseconds

  private onAvatarLangChange(lang) {
    store.dispatch(SELECT_AI_LANG, lang)
    // Accent is tied to the language; drop it when the new language doesn't offer it.
    if (this.accentSelected && !this.accentOptions.some((a) => a.locale === this.accentSelected)) {
      this.onAvatarAccentChange(null);
    }
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide) as Slide;
      newSlide.lang=lang;
      updateCurrentSlide(newSlide);
    }
  }

  // The accent dropdown emits "" for the default option; store it as null.
  private onAccentInput(locale: string) {
    this.onAvatarAccentChange(locale || null);
  }

  private onAvatarVoiceChange(voice) {
    store.dispatch(SELECT_AI_VOICE, voice)
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide) as Slide;
      newSlide.voice=voice;
      updateCurrentSlide(newSlide);
    }
  }

  // Voice chosen from the VoiceSelectorModal. Accent is owned by the panel dropdown now, so we only
  // drop a chosen accent if the new voice's provider no longer supports it.
  private onVoiceValidated({ voice }: { voice: string }) {
    this.onAvatarVoiceChange(voice);
    if (this.accentSelected && !this.accentOptions.some((a) => a.locale === this.accentSelected)) {
      this.onAvatarAccentChange(null);
    }
  }

  private onAvatarAccentChange(accent: string | null) {
    store.dispatch(SELECT_AI_ACCENT, accent);
    if (this.currentSlide) {
      const newSlide = clone(this.currentSlide) as Slide;
      newSlide.accent = accent as AvatarLanguage | null;
      updateCurrentSlide(newSlide);
    }
  }

  private onTextInput() {
    store.dispatch(AVATAR_EDITING_TEXT, this.textToGenerate);
    // update slides
    if(this.currentSlide){
      const newSlide=clone(this.currentSlide);
      newSlide.script=this.textToGenerate;
      updateCurrentSlide(newSlide);
    }
  }


  private goToPayment(){
    this.$router.push({name: 'MyWorkspacePayment'});
  }

  @Emit('generateSlide')
  private generateSlide() {
    return;
  }

  @Emit('generateAllSlide')
  private generateAllSlide() {
    return
  }

  @Watch("currentSlide")
  private onCurrentSlideChange() {
    if (this.currentSlide) {
      this.textToGenerate = this.currentSlide.script
      store.dispatch(AVATAR_EDITING_TEXT, this.currentSlide.script);
      if(this.currentSlide.lang){
        store.dispatch(SELECT_AI_LANG,this.currentSlide.lang)
      }
      store.dispatch(SELECT_AI_ACCENT, this.currentSlide.accent || null)
      this.$nextTick(()=>{
        if(this.currentSlide?.voice){
          store.dispatch(SELECT_AI_VOICE, this.currentSlide.voice)
          this.checkVoiceGender()
        }
      })

    } else {
      this.textToGenerate = "";
    }
    store.dispatch(AVATAR_EDITING_TEXT, this.textToGenerate);
    this.debouncedSSMLCheck();
  }

  // Covers both a new avatar and a layout change that brings an avatar on screen
  // (or takes it away): either way the set of allowed voices moves.
  @Watch("gender")
  private onGenderChange(){
    this.checkVoiceGender()
  }

  @Watch("selectedAvatar")
  private onAvatarChange(){
    this.checkVoiceGender()
  }

  // Re-validate the selected voice if the catalog changes, such as advanced TTS toggling off makes a persisted advanced
  // voice invalid, so it must snap back to a free one.
  @Watch("voicesCatalog")
  private onVoicesCatalogChange(){
    this.checkVoiceGender()
  }

  private checkVoiceGender(){
    // Catalog not loaded yet (or no voice for this gender): don't touch the selection.
    if(this.listOfVoice.length===0){
      return;
    }
    const isVoiceAvaialbleForGender=!!this.listOfVoice.find((v)=>v.value===this.voiceSelected)
    if(!isVoiceAvaialbleForGender) {
      const fallback = this.listOfVoice[0];
      store.dispatch(SELECT_AI_VOICE, fallback.value)
      if(this.currentSlide){
        const newSlide=clone(this.currentSlide) as Slide;
        newSlide.voice=store.getters.getAvatarVoiceSelected;
        updateCurrentSlide(newSlide);
      }
    }
  }

  /***
   ** PUBLIC METHODS
   **/

  // Listening the voice
  public async previewVoice() {
    if (this.playingVoice) {
      this.stopPreviewVoice();
      return;
    } else {
      this.stopPreviewVoice();
    }

    // STOP
    if (this.audioPlayer) {
      this.audioPlayer.stop();
      this.audioPlayer = null;
      store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,false);
      this.playingVoice = false;
      return;
    }
    this.loadingVoice = true;
    const text2SpeachParams = new Text2SpeachParams()
    text2SpeachParams.text = this.textToGenerate;
    text2SpeachParams.voice = this.voiceSelected;
    text2SpeachParams.lang = this.effectiveLang as AvatarLanguage | null;
    try {
      const base64 = await store.dispatch(TEXT_2_SPEECH_GENERATE, text2SpeachParams);
      const url = URL.createObjectURL(base64ToBlob(base64, 'audio/mp3'));
      this.loadingVoice = false;
      this.audioPlayer = new Howl({
        src: [url],
        autoplay: true,
        html5: true,
        onload: () => {
          this.emitTtsUsage(this.audioPlayer?.duration() ?? 0);
        },
        onend: () => {
          store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,false);
          this.playingVoice = false;
        },
        volume: 1,
      });
      this.audioPlayer.play();
      this.playingVoice = true;
      store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,true);
    } catch (e: unknown) {
      this.ssmlErrors = e instanceof Error ? e.message : String(e);
      this.loadingVoice = false;
    }
  }


  private emitTtsUsage(seconds: number): void {
    const credits = ttsCreditsForVoice(this.voiceSelected, seconds);
    if (credits <= 0) return;
    const weetID = store.getters.getEditingWeetID;
    if (!weetID) return;
    const dataEvent = new SpeachEventTtsUsage(weetID, credits);
    window.parent.postMessage(new SpeachEvent(SpeachEventName.TTS_USAGE, dataEvent), "*");
  }

  // Stoping the preview voice
  public async stopPreviewVoice() {

    if (this.audioPlayer) {
      this.audioPlayer.stop();
      this.audioPlayer = null;
      this.playingVoice = false;
      store.dispatch(PREVIEW_TEXT_2_SPEECH_GENERATE,false);

    }
  }

  public async getAudioInformationForText(): Promise<{ seconds: number, blob: Blob }> {
    return new Promise(async (resolve, reject) => {
      this.stopPreviewVoice();
      const text2SpeachParams = new Text2SpeachParams()
      // add pause to avoid the end of the voice too early for avatar (LATENT_SYNC_ISSUE)
      text2SpeachParams.text = this.textToGenerate + formatPauseTag(400);
      text2SpeachParams.voice = this.voiceSelected;
      text2SpeachParams.lang = this.effectiveLang as AvatarLanguage | null;
      try {
        const base64 = await store.dispatch(TEXT_2_SPEECH_GENERATE, text2SpeachParams);
        const blob = base64ToBlob(base64, 'audio/mp3');
        const url = URL.createObjectURL(blob);
        this.audioPlayer = new Howl({
          src: [url],
          autoplay: true,
          html5: true,
          onload: () => {
            this.audioPlayer.stop();
            const seconds = this.audioPlayer.duration();
            this.emitTtsUsage(seconds);
            resolve({seconds: seconds, blob: blob});
          },
          onend: () => {
            this.playingVoice = false;
          },
          volume: 0,
        });
        this.audioPlayer.play();
      } catch (e) {
        this.ssmlErrors = e instanceof Error ? e.message : String(e);
        this.$emit("errorDuringProcess", this.ssmlErrors);
      }
    })
  }

  public insertPause(ms: number): void {
    const textarea = this.$el.querySelector('.contentTextAreaAI textarea') as HTMLTextAreaElement;
    if (!textarea) {
      return;
    }
    const cursorPos = textarea.selectionStart;
    const textBefore = this.textToGenerate.substring(0, cursorPos);
    const textAfter = this.textToGenerate.substring(cursorPos);

    const pauseTag = formatPauseTag(ms);
    this.textToGenerate = textBefore + pauseTag + textAfter;

    this.$nextTick(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos + pauseTag.length, cursorPos + pauseTag.length);
      this.customPause = 2.5;
      this.onTextInput();
    });
  }
}
</script>
<style lang="scss">
#buttonDropDown {
  width: 100%;
  .dropdown-trigger {
    width: 100%;
  }
}
// no scope to manage textarea size
.contentTextAreaAI {
  // fills the space left by the title and the controls below
  flex: 1 1 auto;
  min-height: 120px;
  display: flex;

  .control, .textarea {
    height: 100%;
    width: 100%;
  }

  .textarea {
    resize: none;
  }
  &.overtext{
    .help{
      color: red;
    }
  }
}
</style>
<style lang="scss" scoped>
.avatarContentEditionPanel {
  width: 100%;
  height: 100%;


  .avatarContentEditionContainer {
    align-items: stretch;
    text-align: left;
    font-weight: 700;
    color: var(--black);
    height: 100%;
    display: flex;
    flex-direction: column;

    // everything but the script area keeps its natural height
    > *:not(.contentTextAreaAI) {
      flex: none;
    }

    .avatarContentEditorTitle {
      color: var(--black);
      font-weight: 900;
      font-size: 20px;
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
    }

    .pauseButtonContent {
      color: var(--black);
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 8px;
      display: flex;
    }

    .lineButton{
      margin-top: 8px;
      .buttonOneSlide{
        margin-right: 8px;
      }
      .lineButtonGenerate {
        display: flex;
        align-items: center;
        .iconButtonsGenerate {
          margin-right: 8px;
        }
        .labelButtonGenerate{
          margin-right: 8px;
        }
        &.disabledFreemium{
          .labelButtonGenerate{
            cursor: not-allowed;
            opacity: 0.3;
          }
        }
      }
    }

    .errorSSML {
      margin-top: 16px;
    }

    .lineSelect {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin-top: 16px;

      &.last {
        margin-bottom: 12px;
      }

      .labelSelect {
        position: relative;
        text-align: left;
        font-size: 14px;
        color: var(--black);
        font-weight: 700;
        justify-content: space-between;
        align-items: flex-end;
        display: flex;
        margin-right: 16px;
        min-width: 90px;
      }
      .langSelector, .voiceSelector {
        flex:1;
      }
      .buttonPlayPreview {
        align-content: center;
        margin-left: 8px;
        cursor: pointer;
      }

    }



    .upgradeMessage {
      font-size: 14px;
      display: flex;
      padding-right: 0px;
      color: var(--light2);
    }


    .contentTextAreaAI {
      margin-bottom: 16px;
    }

    .buttonValidateStep {
      position: absolute;
      bottom: 16px;
      right: 16px;
    }
  }

  .pauseCustomTitle {
    font-weight: 700;
  }

  .pauseCustomLine {
    display: flex;
    gap: 8px; /* Adds space between the input and button */
    width: 100%;
    margin-top: 8px; /* Adds some space from the text above */
  }
}
</style>
